// src/pages/admin/viewHistory/fileDownloads.js — browser download + Firebase Storage
// resolution helpers used by ViewHistoryTab. Pure functions: no React state, only
// their arguments and the storage/firestore SDK.
import { storage } from "../../../services/firebase";
import { getBlob, getDownloadURL, ref } from "firebase/storage";

export function saveBlob(blob, filename = "file.bin") {
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(objectUrl);
}

export function sanitizeFilename(filename = "file.bin") {
  return String(filename || "file.bin").replace(/[^\w.\- ]+/g, "_");
}

export function triggerUrlDownload(url, filename) {
  const a = document.createElement("a");
  a.href = url;
  a.download = sanitizeFilename(filename || "file.bin");
  a.target = "_blank";
  a.rel = "noopener noreferrer";
  document.body.appendChild(a);
  a.click();
  a.remove();
}

export function extractStoragePath(url) {
  try {
    const u = new URL(url);
    const oIndex = u.pathname.indexOf("/o/");
    if (oIndex === -1) return null;
    return decodeURIComponent(u.pathname.slice(oIndex + 3));
  } catch {
    return null;
  }
}

export async function resolveStorageDownloadUrl(source) {
  if (source?.url) return source.url;
  if (source?.path) return await getDownloadURL(ref(storage, source.path));
  return "";
}

export async function getPhotoBlob(photo) {
  if (photo?.storagePath)
    return await getBlob(ref(storage, photo.storagePath));
  if (photo?.url) {
    const reconstructed = extractStoragePath(photo.url);
    if (reconstructed) return await getBlob(ref(storage, reconstructed));
    const res = await fetch(photo.url, { cache: "no-store" });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.blob();
  }
  throw new Error("Invalid photo (missing storagePath/url)");
}

export async function downloadSinglePhoto(photo) {
  try {
    const safeName = sanitizeFilename(photo?.name || "photo.jpg");
    const resolvedUrl = await resolveStorageDownloadUrl({
      path: photo?.storagePath || extractStoragePath(photo?.url || ""),
      url: photo?.url || "",
    });

    if (resolvedUrl) {
      triggerUrlDownload(resolvedUrl, safeName);
      return;
    }

    const blob = await getPhotoBlob(photo);
    saveBlob(blob, safeName);
  } catch (err) {
    console.error("Download photo failed:", err);
    alert(
      "Failed to download photo. Please check the Storage bucket CORS settings for the deployed domain."
    );
  }
}

export function getDocketSource(d, signed = false) {
  if (!d) return { url: "", path: "", name: "", contentType: "" };
  if (signed) {
    return {
      url: d.signedDocketUrl || "",
      path: d.signedDocketStoragePath || "",
      name: d.signedDocketFileName || "",
      contentType: d.signedDocketContentType || "",
    };
  }
  return {
    url: d.docketUrl || "",
    path: d.docketStoragePath || "",
    name: d.docketFileName || "",
    contentType: d.docketContentType || "",
  };
}

export async function openOrDownloadStoredFile(source, fallbackName = "file") {
  if (!source?.url && !source?.path) {
    alert("File not found.");
    return;
  }

  const filename = sanitizeFilename(source?.name || fallbackName);

  try {
    const resolvedUrl = await resolveStorageDownloadUrl(source);
    if (resolvedUrl) {
      triggerUrlDownload(resolvedUrl, filename);
      return;
    }
  } catch (err) {
    console.warn("Direct storage URL fallback failed:", err);
  }

  if (source.path) {
    const blob = await getBlob(ref(storage, source.path));
    saveBlob(blob, filename);
    return;
  }

  window.open(source.url, "_blank", "noopener,noreferrer");
}

export function getConfirmationSource(d) {
  if (!d) return { url: "", path: "" };
  const cc = d.clientConfirmation || d.customerConfirmation || {};
  const url =
    d.clientConfirmationPdfUrl ||
    d.confirmationPdfUrl ||
    cc.pdfUrl ||
    cc.url ||
    "";
  const path =
    d.clientConfirmationPdfPath ||
    d.confirmationPdfPath ||
    cc.pdfPath ||
    cc.path ||
    "";
  return { url, path };
}
